import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import DataTable from '../../components/DataTable/DataTable';
import { IColInfo } from '../../components/DataTable/Interfaces';
import SwitchField from '../../components/Fields/SwitchField';
import { FormConfig } from '../../components/Fields/Types';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from "../../components/Spinner/SpinnerOverlay";
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import UserModel from '../../models/UserModel';
import DataService from '../../services/data.service';
import Button from './../../components/Buttons/Button';
import ButtonBar from './../../components/Fields/ButtonBar';
import CheckboxField from './../../components/Fields/CheckboxField';
import Form from './../../components/Fields/Form';
import TextboxField from './../../components/Fields/TextboxField';
import Unauthorized from './../Authentication/Unauthorized';

interface UserSearchCriteria {
    quickSearch: string
    active: boolean
    rememberFilter: boolean
}

const UserSearchView: React.FC = () => {
    const [users, setUsers] = useState<UserModel[]>([]);
    const [showSpinner, setShowSpinner] = useState<boolean>(false);
    const { handleException } = useNotifications();
    const [formData, setFormData] = useState<UserSearchCriteria>({
        quickSearch: '',
        active: true,
        rememberFilter: false,
    });

    useEffect(() => {
        const filterItem = localStorage.getItem("userFilter");
        if (filterItem !== null) {
            let filter: UserSearchCriteria = JSON.parse(filterItem);
            setFormData(filter);
            search()
        }
    }, [])

    function search() {
        setShowSpinner(true);

        if (formData.rememberFilter)
            localStorage.setItem('userFilter', JSON.stringify(formData));
        else
            localStorage.removeItem(`userFilter`);

        DataService
            .post<UserModel[]>(`api/v1/Users/Search/`, formData)
            .then(response => {
                setShowSpinner(false);
                setUsers(response);
            })
            .catch(e => {
                setShowSpinner(false);
                handleException(e)
            });
    }

    function setDefaultCriteria() {
        let defaultCriteria = {
            quickSearch: '',
            active: true,
            rememberFilter: formData?.rememberFilter ?? false,
        }
        setFormData({ ...defaultCriteria })
    }

    function onFieldChanged(name: string, value: string): void {
        formData[name] = value;
        setFormData(formData);

        console.log(name + ': ' + value);
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'quickSearch', label: 'Search', labelSize: 2 },
            { name: 'active', label: 'Active users only', labelSize: 2 },
            { name: 'rememberFilter', label: 'Remember search criteria', labelSize: 2 },
        ],
        handleSubmit: search,
        handleFieldChanged: onFieldChanged,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData);

    const colInfos: IColInfo[] = [
        { dataProp: 'thumbnailUrl', dataType: 'thumbnail', width: 40, sort: false },
        { title: 'Id', key: true, dataProp: 'id', dataType: 'number' },
        { title: 'Name', dataProp: 'name', sort: 'asc', filterInfo: { filterType: 'Textbox' } },
        { title: 'Email', dataProp: 'email', filterInfo: { filterType: 'Textbox' } },
        { title: 'Language', dataProp: 'language', filterInfo: { filterType: 'CheckboxList' } },
        { title: 'Locked', dataProp: 'isLocked', dataType: 'bool', filterInfo: { filterType: 'CheckboxList' } },
        { title: 'Enabled', dataProp: 'isEnabled', dataType: 'bool', filterInfo: { filterType: 'CheckboxList' } },
    ];

    const { isAuthorizedFor } = useAuth();
    if (!isAuthorizedFor(ApplicationFunctions.ViewUser)) {
        return (<Unauthorized />);
    }

    return (
        <div className='main-content'>
            <Form title='Users' footer={(<>
                <ButtonBar offset={2}>
                    <Button text='Search' {...getSubmitButtonProps()} />
                    <Button text='Clear' type='button' onClick={setDefaultCriteria} />
                </ButtonBar>
            </>)}>
                <Container fluid>
                    <TextboxField {...getFieldProps('quickSearch')} />
                    <SwitchField {...getFieldProps('active')} />
                    <SwitchField {...getFieldProps('rememberFilter')} />
                </Container>
            </Form>
            <div style={{ margin: 12 }}>
                <DataTable
                    name='dtUsers'
                    colInfos={colInfos}
                    tableData={users}
                    navigateTitle='User'
                    navigateUrl='/plm/UserManagement/User/'
                />
            </div>
            {showSpinner && (<SpinnerOverlay />)}
        </div>
    );
}
export default UserSearchView;